//library
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";

import api from "services/api";

//services
import getSolutionCategory from "services/get-solution-category";

//common
import formatIndexNumber from "common/format-index-number";
import serveRequestErrors from "common/serve-request-errors";

//constants
import { statusOptions } from "constants/status-options";

//dto
import ErrorResponseDto from "dto/services/error-response-dto";
import { PageSolutionsTableDto } from "dto/pages/solutions/page-solutions-dto";
import SolutionsTableProps, { SolutionsDataDto, SolutionsUpdateDto } from "dto/components/pages/solutions/page-solutions-table-dto";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//assets
import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";

const SolutionTable: React.FC<SolutionsTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdateData, onCreateData, onHandlePageChange, fetching, paginationTable, id, selectedCategoryId }) => {
	const tableRef = useRef<HTMLDivElement | null>(null);
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(true);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [solutionData, setSolutionData] = useState<SolutionsDataDto>(data);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const [solutionUpdatedData, setSolutionUpdatedData] = useState<SolutionsUpdateDto>();
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const isEmptyData = useMemo(() => solutionData.length === 0 && !fetching, [solutionData.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "logo" | "title" | "category" | "status", value: string) => {
		const updatedSolution = solutionUpdatedData ?? solutionData.find((solution) => solution.id === id);

		if (updatedSolution) {
			const updatedData = { ...updatedSolution, [field]: value};

			setSolutionUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
	}, [solutionData, solutionUpdatedData]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["title", "category", "logo", "status"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (solutionUpdatedData) {
			const rowErrors = validateRow(solutionUpdatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return;
			}

			const newSolution = solutionData.map((solution) => solution.id === id ? { ...solution, ...solutionUpdatedData } : solution);

			setIsReadyData(false);
			setSolutionData(newSolution);
			onUpdateData(solutionUpdatedData);
			setEditingRow(null);
			setSolutionUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} }));
		}
	}, [onUpdateData, setDisableAddNew, solutionData, solutionUpdatedData]);

	const updateSolutionData = useCallback((newSolutionData: PageSolutionsTableDto[], updatedSolution: PageSolutionsTableDto, newSortNumber?: number) => {
		const updatedData = { ...updatedSolution, sortNumber: newSortNumber ?? updatedSolution.sortNumber };
		setSolutionUpdatedData(updatedData);
		setSolutionData(newSolutionData);
	}, []);

	//prettier-ignore
	const fetchPageData = useCallback(async (targetPage: number): Promise<PageSolutionsTableDto[]> => {
		try {
			const payload = { size: paginationTable.tableSize, page: targetPage };
			const response = await api.get.solution.servicesCard(selectedCategoryId, payload);
			return response?.data?.data?.list?.content || [];
		} catch (error) {
			const err = error as Error | ErrorResponseDto;
			serveRequestErrors(err);
			return []; 
		}
	}, [paginationTable.tableSize, selectedCategoryId]);

	//prettier-ignore
	const onHandleMove = useCallback(async (id: string, move: number) => {
		if (selectedCategoryId === "all") return;

		const index = solutionData.findIndex((solution) => solution.id === id);
		const updatedSolution = solutionUpdatedData ?? solutionData[index];
		setEditingRow(id);
		setDisableAddNew(true);

		const isFirstItem = index === 0 && move === -1;
		const isLastItem = index === solutionData.length - 1 && move === 1;
		const moveToPrevPage = isFirstItem && paginationTable.currentPage > 0;
		const moveToNextPage = isLastItem && paginationTable.currentPage < paginationTable.totalPages - 1;

		if (!isFirstItem && !isLastItem) {
			const newSolution = [...solutionData];
			newSolution.splice(index, 1);
			const movedDataSortNumber = solutionData[index + move].sortNumber;

			newSolution.splice(index + move, 0, updatedSolution);
			updateSolutionData(newSolution, updatedSolution, movedDataSortNumber);
		}  else if (moveToPrevPage || moveToNextPage) {
			const targetPage = moveToPrevPage ? paginationTable.currentPage - 1 : paginationTable.currentPage + 1;
			const isNextPage = move === 1;
			const pageData = await fetchPageData(targetPage);
	
			if (pageData.length > 0) {
			  const targetItem = isNextPage ? pageData[0] : pageData[pageData.length - 1];
			  const newSolutionData = isNextPage ? [updatedSolution, ...pageData.slice(1)] : [...pageData.slice(0, -1), updatedSolution];

			  updateSolutionData(newSolutionData, updatedSolution, targetItem.sortNumber);
			  onHandlePageChange(targetPage);
			}
		}
	}, [selectedCategoryId, solutionData, solutionUpdatedData, setDisableAddNew, paginationTable, updateSolutionData, fetchPageData, onHandlePageChange]);

	//prettier-ignore
	const onHandleDelete = useCallback((id: string) => {
		const updatedSolution = solutionData.find((solution) => solution.id === id);

		if (updatedSolution) {
			const updatedData = { ...updatedSolution, status: "deleted", };
			const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

			onUpdateData(updatedData, changePage);
		}

		setEditingRow(null);
		setSolutionUpdatedData(null);
		setDisableAddNew(false);
	}, [onUpdateData, paginationTable.currentPage, paginationTable.numberOfElements, setDisableAddNew, solutionData]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const updatedData = { logo: newRow?.logo || "", title: newRow?.title || "", category: newRow?.category || "", status: "active" };

		onCreateData(updatedData);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	const cancelEditing = useCallback(() => {
		setEditingRow(null);
		setSolutionUpdatedData(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, [editingRow || ""]: {} }));
	}, [setDisableAddNew, editingRow]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (editingRow && tableRef.current && !tableRef.current.contains(event.target as Node)) cancelEditing();
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [editingRow, cancelEditing]);

	useEffect(() => {
		if (!fetching) {
			setIsReadyData(true);
		} else {
			setEditingRow(null);
		}
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setSolutionData(data);
	}, [data, editingRow, isReadyData]);

	useEffect(() => {
		if (solutionUpdatedData && solutionUpdatedData.id) setEditingRow(solutionUpdatedData.id);
	}, [solutionUpdatedData]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-solutions-table">
			<div className="solutions-table">
				<div className="solutions-table__wrapper" ref={tableRef}>
					<table className="solutions-table__container">
						<thead className="solutions-table__header">
							<tr>
								<th className="solutions-table__th">No.</th>
								<th className="solutions-table__th">Icon</th>
								<th className="solutions-table__th">Title</th>
								<th className="solutions-table__th">Category</th>
								<th className="solutions-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="solutions-table__content">
							{solutionData.map((item, index) => {
								const isEditing = editingRow === item.id;
								const isExpanded = expandedRows.includes(item.id);
								const rowErrors = errors[item.id] || {};
								const isAllCategory = selectedCategoryId === "all";

								return (
									<tr className="solutions-table__tr" key={`${item.id}-${index}`}>
										<td className="solutions-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="solutions-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="logo" error={rowErrors.logo} touched={!!solutionUpdatedData} value={solutionUpdatedData?.logo ?? item.logo} maxRows={100} rows={5} onChange={(e) => onHandleInputChange(item.id, "logo", e.target.value)} />
											) : isExpanded ? (
												item.logo
											) : (
												truncateText(item.logo, 30)
											)}
										</td>

										<td className="solutions-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!solutionUpdatedData} value={solutionUpdatedData?.title ?? item.title} maxCharLength={90} rows={5} maxRows={20} onChange={(e) => onHandleInputChange(item.id, "title", e.target.value)} />
											) : isExpanded ? (
												item.title
											) : (
												truncateText(item.title, 30)
											)}
										</td>

										<td className="solutions-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="category" error={rowErrors.category} touched={!!solutionUpdatedData} placeholder={item.categoryName} value={solutionUpdatedData?.category ?? item.category} onChange={(e) => onHandleInputChange(item.id, "category", e.target.value)} loadOptions={() => getSolutionCategory(id ?? "")} searchable={false} />
											) : (
												item.categoryName
											)}
										</td>

										<td className="solutions-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="status" error={rowErrors.status} touched={!!solutionUpdatedData} placeholder={item.status} value={solutionUpdatedData?.status ?? item.status} onChange={(e) => onHandleInputChange(item.id, "status", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={item.status === "active" ? "solutions-table__td--active" : "solutions-table__td--inactive"}>{getLabel(statusOptions, item.status)}</span>
											)}
										</td>

										<td className="solutions-table__td">
											{isEditing ? (
												<div className="solutions-table__edit">
													<button className="solutions-table__icon-button--bordered" onClick={() => onHandleDelete(item.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button>

													{!isAllCategory && (
														<>
															<button className="solutions-table__icon-button--bordered" onClick={() => onHandleMove(item.id, -1)}>
																<img src={arrowUp} alt="up" />
															</button>

															<button className="solutions-table__icon-button--bordered" onClick={() => onHandleMove(item.id, 1)}>
																<img src={arrowDown} alt="down" />
															</button>
														</>
													)}

													<button className="solutions-table__icon-button--save" onClick={() => onHandleSave(item.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="solutions-table__actions">
													<button className="solutions-table__icon-button" onClick={() => toggleRow(item.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="solutions-table__icon-button" onClick={() => onHandleEdit(item.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="solutions-table__tr" ref={newRowRef}>
									<td className="solutions-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="solutions-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-logo" error={errors["new"]?.logo} touched={!!newRow} value={newRow?.logo || ""} maxRows={100} placeholder="Enter URL" onChange={(e) => onHandleNewInputChange("logo", e.target.value)} />
									</td>

									<td className="solutions-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-title" error={errors["new"]?.title} touched={!!newRow} value={newRow?.title || ""} maxCharLength={90} maxRows={20} placeholder="Enter text" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="solutions-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="new-category" error={errors["new"]?.category} touched={!!newRow} placeholder="Please Select" value={newRow?.category || ""} onChange={(e) => onHandleNewInputChange("category", e.target.value)} loadOptions={() => getSolutionCategory(id ?? "")} searchable={false} />
									</td>

									<td className="solutions-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="status" error={errors["new"]?.status} touched={!!newRow} placeholder="Please Select" value={newRow?.status || ""} onChange={(e) => onHandleNewInputChange("status", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="solutions-table__td">
										<div className="solutions-table__actions">
											<button className="solutions-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="solutions-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="solutions-table__tr">
									<td className="solutions-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}

							{isEmptyData && (
								<tr className="solutions-table__tr">
									<td className="solutions-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default SolutionTable;
